import axios from 'axios'
import Cookies from 'js-cookie'
import { API_BASE_URL } from '../config'
import type {
  User,
  AuthResponse,
  RequestCodeRequest,
  VerifyCodeRequest,
  VerifyCodeOnlyRequest,
  VerifyCodeOnlyResponse,
  CompleteAuthRequest,
  UserUpdateRequest,
  Animal,
  CreateAnimalRequest,
  Service,
  CreateServiceRequest,
  ServiceSearchQuery,
  ServiceType,
  AnimalType,
  Conversation,
  Message,
  SendMessageRequest,
  PendingContentResponse,
} from '../types'

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = Cookies.get('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear auth token on 401
      Cookies.remove('auth_token')
      // Optionally redirect to login
      if (window.location.pathname !== '/auth') {
        window.location.href = '/auth'
      }
    }
    return Promise.reject(error)
  }
)

// Auth API
export const authApi = {
  requestCode: async (data: RequestCodeRequest): Promise<{ message: string }> => {
    const response = await api.post('/auth/request-code', data)
    return response.data
  },

  verifyCodeOnly: async (data: VerifyCodeOnlyRequest): Promise<VerifyCodeOnlyResponse> => {
    const response = await api.post('/auth/verify-code-only', data)
    return response.data
  },

  completeAuth: async (data: CompleteAuthRequest): Promise<AuthResponse> => {
    const response = await api.post('/auth/complete-auth', data)
    return response.data
  },

  verifyCode: async (data: VerifyCodeRequest): Promise<AuthResponse> => {
    const response = await api.post('/auth/verify-code', data)
    return response.data
  },

  logout: async (): Promise<{ message: string }> => {
    const response = await api.post('/auth/logout')
    return response.data
  },
}

// User API
export const userApi = {
  getProfile: async (): Promise<User> => {
    const response = await api.get('/users/profile')
    return response.data
  },

  updateProfile: async (data: UserUpdateRequest): Promise<User> => {
    const response = await api.put('/users/profile', data)
    return response.data
  },

  searchUsers: async (query: string): Promise<User[]> => {
    const response = await api.get('/users/search', { params: { q: query } })
    return response.data
  },
}

// Animal API
export const animalApi = {
  getAnimals: async (): Promise<Animal[]> => {
    const response = await api.get('/animals')
    return response.data
  },

  searchAnimals: async (params: {
    animal_type?: AnimalType
    city?: string
    latitude?: number
    longitude?: number
    max_distance_km?: number
    sort_by?: 'relevance' | 'distance' | 'recent'
    limit?: number
    offset?: number
  }): Promise<Animal[]> => {
    const response = await api.get('/animals/search', { params })
    return response.data
  },

  createAnimal: async (data: CreateAnimalRequest): Promise<Animal> => {
    const response = await api.post('/animals', data)
    return response.data
  },

  updateAnimal: async (id: string, data: Partial<CreateAnimalRequest>): Promise<Animal> => {
    const response = await api.put(`/animals/${id}`, data)
    return response.data
  },

  deleteAnimal: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/animals/${id}`)
    return response.data
  },
}

// Service API
export const serviceApi = {
  getServices: async (): Promise<Service[]> => {
    const response = await api.get('/services')
    return response.data
  },

  searchServices: async (params: ServiceSearchQuery): Promise<Service[]> => {
    const response = await api.get('/services/search', { params })
    return response.data
  },

  createService: async (data: CreateServiceRequest): Promise<Service> => {
    const response = await api.post('/services', data)
    return response.data
  },

  updateService: async (id: string, data: Partial<CreateServiceRequest>): Promise<Service> => {
    const response = await api.put(`/services/${id}`, data)
    return response.data
  },

  deleteService: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/services/${id}`)
    return response.data
  },
}

// Message API
export const messageApi = {
  getConversations: async (): Promise<Conversation[]> => {
    const response = await api.get('/messages/conversations')
    return response.data
  },

  getMessages: async (conversationId: string): Promise<Message[]> => {
    const response = await api.get(`/messages/${conversationId}`)
    return response.data
  },

  sendMessage: async (data: SendMessageRequest): Promise<Message> => {
    const response = await api.post('/messages', data)
    return response.data
  },
}

// Admin API
export const adminApi = {
  getPendingContent: async (): Promise<PendingContentResponse> => {
    const response = await api.get('/admin/pending')
    return response.data
  },

  reviewAnimal: async (animalId: string, action: 'approve' | 'reject'): Promise<Animal> => {
    const response = await api.post(`/admin/animals/${animalId}/review`, { action })
    return response.data
  },
}

// Health check
export const healthApi = {
  check: async (): Promise<{ status: string; timestamp: string }> => {
    const response = await api.get('/health')
    return response.data
  },
}

// Search API - unified search for services, animals, and content
export const searchApi = {
  searchAll: async (params: {
    query?: string
    content_type?: 'services' | 'animals' | 'all' // new parameter for content type
    service_type?: ServiceType
    animal_type?: AnimalType
    city?: string
    latitude?: number
    longitude?: number
    max_distance_km?: number
    max_price?: number
    sort_by?: 'relevance' | 'distance' | 'recent' | 'price_low' | 'price_high'
    need?: boolean // for filtering services by need/offer
    limit?: number
    offset?: number
  }): Promise<{ services: Service[], animals: Animal[], total: number }> => {
    const searchParams = new URLSearchParams()
    
    if (params.content_type) searchParams.append('content_type', params.content_type)
    if (params.service_type) searchParams.append('service_type', params.service_type)
    if (params.animal_type) searchParams.append('animal_type', params.animal_type)
    if (params.city) searchParams.append('city', params.city)
    if (params.latitude !== undefined) searchParams.append('latitude', params.latitude.toString())
    if (params.longitude !== undefined) searchParams.append('longitude', params.longitude.toString())
    if (params.max_distance_km !== undefined) searchParams.append('max_distance_km', params.max_distance_km.toString())
    if (params.max_price !== undefined) searchParams.append('max_price', params.max_price.toString())
    if (params.sort_by) searchParams.append('sort_by', params.sort_by)
    if (params.need !== undefined) searchParams.append('need', params.need.toString())
    if (params.limit !== undefined) searchParams.append('limit', params.limit.toString())
    if (params.offset !== undefined) searchParams.append('offset', params.offset.toString())

    const response = await api.get(`/search?${searchParams.toString()}`)
    return response.data
  }
}

export default api